import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { BaseConfigurator } from '../configurator/base-configurator';
import { CarpentryService } from '../../../services/configurators-2d/applications/carpentry.service';
import { Configuration } from '../../../models/configurators-2d/applications/carpentry/configuration';
import { ConfiguratorStructure } from '../configurator/configurator-structure';
import { ActivatedRoute } from '@angular/router';
import { CA_ACCESSORIES, CA_CONFIGURATION, CA_SUMMARY } from '../../../providers/carpentry.providers';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { ShoppingCartAssistance } from 'src/app/models/shopping-cart/shopping-cart-assistance';

@Component({
  selector: 'ng-carpentry-configuration',
  templateUrl: './carpentry.component.html',
})
export class CarpentryComponent extends BaseConfigurator<Configuration, CarpentryService> implements OnInit {
  /** Etape de la configuration */
  @Input() step: number = 1;

  /** Rechargement terminé */
  @Output() setGlobalLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

  /** Changement de page */
  @Output() changePage: EventEmitter<any> = new EventEmitter<boolean>();

  /** quotationRecord en cours */
  selectedQuotationRecordId: number;

  /** En cours de chargement */
  isLoading: boolean = false;

  /** Rechargement des components enfants */
  refresh: boolean = false;

  constructor(
    private readonly route: ActivatedRoute,
    public carpentryService: CarpentryService,
    public modalService: ModalService,
    injector: Injector
  ) {
    super(carpentryService, injector);
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.loadExistingConfigurationIfNeeded(this.route, (projectIdChange: boolean) => {
      if (projectIdChange) this.refresh = true;
    }).then(() => {
      this.setGlobalLoading.emit(false);
      this.isLoading = false;
    });
  }

  /** Fin du rechargement des components enfants */
  refreshFinishedSet(): void {
    this.refresh = false;
  }

  /**
   * Fonction pour passer à l'étape suivante en gardant en mémoire le quotationRecord en cours
   * @param quotationRecordId
   */
  nextStep(quotationRecordId: number): void {
    this.selectedQuotationRecordId = quotationRecordId;
    this.changePage.emit(this.step + 1);
  }

  /**
   * Fonction pour revenir autant de pages en arrière que numberPagePrevious
   * @param eventData
   */
  previousStep(eventData: { externalId: number; numberPagePrevious: number }): void {
    this.selectedQuotationRecordId = eventData.externalId;
    this.changePage.emit(this.step - eventData.numberPagePrevious);
  }

  /**
   * Réinitialisation du selectedQuotationRecordId
   */
  resetQuotationRecordId(): void {
    this.selectedQuotationRecordId = undefined;
  }

  /**
   * Fonction pour vérifier s'il y a un service de pose
   * @returns
   */
  override async checkInstallation(): Promise<boolean> {
    if (this.configuration.installationReminder) {
      if (this.configuration.assistances != undefined) {
        if (!this.configuration.assistances.some(a => a.assistanceType.label === 'Pose')) {
          return await this.installationReminder();
        }
      } else {
        return await this.installationReminder();
      }
    }
    return true;
  }

  /**
   * Fonction pour afficher la modale rappellant l'absence de service de pose
   * @returns
   */
  async installationReminder(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.modalService.display(MODAL.SHOPPING_CART_INSTALLATION_REMINDER, {
        onClose: async result => {
          this.modalService.close();
          if (result === 1) {
            //cas oui
            this.configuration.installationReminder = false;
            resolve(await this.addInstallation());
          } else if (result === 0) {
            //cas non
            this.configuration.installationReminder = false;
            resolve(true);
          } else {
            //cas plus tard
            resolve(true);
          }
        },
      });
    });
  }

  /**
   * Fonction affichant la modale pour saisir un service de pose
   * @returns
   */
  async addInstallation(): Promise<boolean> {
    const assistances = await this.carpentryService.getAssistances();
    const newAssistance: ShoppingCartAssistance = {
      assistanceType: assistances.find(a => a.label === 'Pose'),
    };

    return new Promise((resolve, reject) => {
      this.modalService.display(MODAL.SHOPPING_CART_ADD_ASSISTANCE, {
        existingAssistances: this.configuration.assistances,
        appService: this.carpentryService,
        editAssistance: newAssistance,
        onClose: assistance => {
          this.modalService.close();
          if (assistance != undefined) {
            this.configuration.assistances ??= [];
            this.configuration.assistances.push(assistance);
            resolve(false);
          } else {
            resolve(false);
          }
        },
      });
    });
  }

  /**
   * Fonction de vérification de la commande
   * @returns
   */
  override async checkOrder(): Promise<boolean> {
    const checkResult = await this.service.checkOrderBeforeSend();
    if (!checkResult.isValid && checkResult.modalId) {
      this.modalService.display(checkResult.modalId, {
        ...checkResult.modalData,
        onClose: () => {
          this.modalService.close();
        },
      });
    }

    return checkResult.isValid;
  }

  ngOnDestroy() {
    this.service.reset();
  }

  initConfigurator(): ConfiguratorStructure {
    return new ConfiguratorStructure([
      {
        index: 1,
        title: 'carpentry.steps.configuration',
        injectionToken: CA_CONFIGURATION,
        gtmTitle: 'Menuiserie | Configuration',
        gtmPageName: '',
      },
      {
        index: 2,
        title: 'carpentry.steps.accessories',
        injectionToken: CA_ACCESSORIES,
        gtmTitle: 'Menuiserie | Accessoires',
        gtmPageName: 'accessoires',
      },
      {
        index: 3,
        title: 'carpentry.steps.summary',
        injectionToken: CA_SUMMARY,
        gtmTitle: 'Menuiserie | Résumé et prix',
        gtmPageName: 'prix-et-resume',
      },
    ]);
  }

  /** Fonction de désactivation du bouton Enregistrer */
  override isSaveProjectButtonDisabled(): boolean {
    return false;
  }

  /**
   * Fonction de masquage du bouton suivant
   * @param step
   * @param finalStep
   * @returns
   */
  override isNextButtonHidden(step: number, finalStep: number): boolean {
    if (step < finalStep && this.configuration.quotationRecords) {
      return false;
    }
    return true;
  }
}
